/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { Map, List } from 'immutable';
import { Reports } from 'actions/sharedJsActions';

// import directly from the interface to avoid circular dependency
import { Hooks } from '../../shared/interface/sharedJsInterface';

// Hooks
const { Loading } = Hooks;
const { useIsLoading, useIsLoaded } = Loading;

// Actions
const { reportsFetch, memberGroupReportsFetch } = Reports;

export const useUsageReportData = (reportId, questionBankId) => {
  const reports = useSelector(state => state.reports);
  const report = reports.get(reportId);
  const startDate = report?.get('start_date');
  const endDate = report?.get('end_date');
  const organizationName = report?.get('organization_name');
  const reportData = report?.get('report_data');
  const organizationBankUsage = reportData?.organization_banks_usage;
  const questionBankUsage = organizationBankUsage ? organizationBankUsage[questionBankId] : null;
  const organizationSubjectsUsage = reportData?.organization_subjects_usage;
  const questionBankSubjectsUsage = organizationSubjectsUsage ? organizationSubjectsUsage[questionBankId] : null;

  return {
    startDate,
    endDate,
    organizationName,
    questionBankUsage,
    questionBankSubjectsUsage
  };
};

export const useClassReportData = (reportId) => {
  const reports = useSelector(state => state.reports);
  const report = reports.get(reportId);
  const startDate = report?.get('start_date');
  const endDate = report?.get('end_date');
  const reportData = report?.get('report_data');
  const memberGroupUsage = reportData?.member_group_usage;
  const questionBankUsage = memberGroupUsage?.bank_usage;
  const studentUsage = memberGroupUsage?.student_usage;
  const memberGroupSubjectUsage = reportData?.member_group_subjects_usage?.subjects_usage;

  const subjectData = memberGroupSubjectUsage?.map(subject => new Map({
    subjectName: subject.name,
    id: subject.id,
    correct: subject.total_questions_correct_test,
    incorrect: subject.total_questions_answered_test - subject.total_questions_correct_test,
    partiallyCorrect: subject.total_answered_partially_correct_test,
    partiallyCorrectStudy: subject.total_answered_partially_correct,
    time: subject.time,
    correctStudy: subject.total_questions_correct_all,
    incorrectStudy: subject.total_questions_answered_all - subject.total_questions_correct_all,
    timeStudy: subject.time_study,
  }));

  return {
    startDate,
    endDate,
    questionBankUsage,
    subjectData,
    studentUsage
  };
};

export const useLoadReportsIfNeeded = () => {
  const dispatch = useDispatch();
  const session = useSelector(state => state.session);
  const organizationId = session.get('selected_organization_id');
  const key = reportsFetch.getKey(organizationId);
  const isLoaded = useIsLoaded(key);
  const isLoading = useIsLoading(key);

  useEffect(() => {
    if (!isLoaded && !isLoading) dispatch(reportsFetch(organizationId));
  }, [isLoaded, isLoading, organizationId]);

  return !isLoaded || isLoading;
};

export const getPercentage = (num, dem) => {
  if (!dem) return 0;
  return Math.round((num || 0) / dem * 100);
};

export const redirectOnChangeOrganization = (organizationId) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!selectedOrganizationId) {
      setSelectedOrganizationId(organizationId);
    } else if (selectedOrganizationId && organizationId) {
      if (selectedOrganizationId !== organizationId) navigate('/usage-reports');
    }
  }, [organizationId]);
};

export const useLoadMemberGroupReportsIfNeeded = (organizationId, userId, classId) => {
  const dispatch = useDispatch();
  const key = memberGroupReportsFetch.getKey(organizationId, userId, classId);
  const isLoaded = useIsLoaded(key);
  const isLoading = useIsLoading(key);

  useEffect(() => {
    if (!isLoaded && !isLoading) dispatch(memberGroupReportsFetch(organizationId, userId, classId));
  }, [isLoaded, isLoading, organizationId, classId, userId]);

  return !isLoaded || isLoading;
};
